import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FC, ReactNode, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import SidePanel from 'src/components/design-system/SidePanel';
import useWindowSize from 'src/hooks/useWindowSize';
import { shouldShowShareCopyOverflowTooltip } from 'src/sections/view_shopping_lists/shareCopyOverflowTooltip';

export interface ActionSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  /** Custom tooltip content for the truncated header title. Defaults to `title`. */
  titleTooltip?: string;
  /** When false, the header title does not show a tooltip. Defaults to true. */
  showTitleTooltip?: boolean;
  children?: ReactNode;
  onSave?: () => void | Promise<void>;
  onCancel?: () => void;
  saveLabel?: string;
  cancelLabel?: string;
  saveDisabled?: boolean;
  cancelDisabled?: boolean;
  saveLoading?: boolean;
  /** Shown on hover when the Save button is disabled */
  saveDisabledTooltip?: ReactNode;
  /** Additional controls rendered in the centered action bar before Save/Cancel */
  headerActions?: ReactNode;
  width?: string;
  position?: 'left' | 'right' | 'bottom';
  className?: string;
  contentClassName?: string;
  closeOnEsc?: boolean;
  showSave?: boolean;
  showCancel?: boolean;
  showClose?: boolean;
}

const ActionSidePanel: FC<ActionSidePanelProps> = ({
  isOpen,
  onClose,
  title,
  titleTooltip,
  showTitleTooltip = true,
  children,
  onSave,
  onCancel,
  saveLabel,
  cancelLabel,
  saveDisabled = false,
  cancelDisabled = false,
  saveLoading = false,
  saveDisabledTooltip,
  headerActions,
  width,
  position = 'right',
  className = 'bg-[#F1F1F1]',
  contentClassName = '',
  closeOnEsc = true,
  showSave = true,
  showCancel = true,
  showClose = true,
}) => {
  const { t } = useTranslation();
  const { width: viewportWidth } = useWindowSize();

  const panelWidth = useMemo(
    () => width ?? (viewportWidth >= 768 ? '900px' : '100%'),
    [viewportWidth, width]
  );

  const handleCancelClick = useCallback(() => {
    if (onCancel) {
      onCancel();
      return;
    }
    onClose();
  }, [onCancel, onClose]);

  const handleSaveClick = useCallback(() => {
    onSave?.()?.catch((err: unknown) => {
      console.error('[ActionSidePanel] onSave failed', err);
    });
  }, [onSave]);

  const resolvedSaveLabel = saveLabel ?? t('Save');
  const resolvedCancelLabel = cancelLabel ?? t('Cancel');
  const isSaveDisabled = saveDisabled || saveLoading;
  const hasActionBar = Boolean(headerActions) || showSave || showCancel;
  const canShowTitleTooltip =
    showTitleTooltip && shouldShowShareCopyOverflowTooltip(title, viewportWidth);

  const saveButton = (
    <Button
      color="primary"
      size="md"
      onClick={handleSaveClick}
      disabled={isSaveDisabled}
      loading={saveLoading}
    >
      {resolvedSaveLabel}
    </Button>
  );

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={onClose}
      closeOnEsc={closeOnEsc}
      width={panelWidth}
      position={position}
      className={className}
      fullHeight
    >
      <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <div className="grid min-h-[50px] w-full shrink-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 overflow-hidden bg-info px-[32px] py-[8px]">
          <span
            className="min-w-0 truncate pr-2 font-bold text-white"
            {...(canShowTitleTooltip
              ? {
                  'data-tooltip-id': 'global-tooltip',
                  'data-tooltip-content': titleTooltip ?? title,
                }
              : {})}
          >
            {title}
          </span>
          {showClose && (
            <button
              type="button"
              data-testid="action-side-panel-close"
              className="k-button k-button-md k-button-flat k-button-flat-base k-rounded-md k-icon-button k-window-action k-dialog-action !text-white"
              onClick={onClose}
              aria-label={t('Close')}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          )}
        </div>
        {hasActionBar && (
          <div
            data-testid="action-side-panel-actions"
            className="flex shrink-0 items-center justify-center gap-2 px-[32px] py-4"
          >
            {headerActions}
            {showSave &&
              (saveDisabledTooltip ? (
                <PopUp
                  popupAlign={{ horizontal: 'center', vertical: 'bottom' }}
                  anchorAlign={{ horizontal: 'center', vertical: 'top' }}
                  className="max-h-[180px]"
                  content={<div className="m-1 max-w-[350px]">{saveDisabledTooltip}</div>}
                  show={isSaveDisabled}
                >
                  {saveButton}
                </PopUp>
              ) : (
                saveButton
              ))}
            {showCancel && (
              <Button
                color="secondary"
                size="md"
                onClick={handleCancelClick}
                disabled={cancelDisabled || saveLoading}
              >
                {resolvedCancelLabel}
              </Button>
            )}
          </div>
        )}
        <div
          className={`min-h-0 flex-1 overflow-x-hidden ${contentClassName || 'overflow-y-auto'}`}
        >
          {children}
        </div>
      </div>
    </SidePanel>
  );
};

export default ActionSidePanel;
